import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import type { LegalRepresentative } from "@/lib/api/review";
import { DetailField, FieldRejectedBadge, type FieldRejectionInfo } from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

interface LegalRepresentativeTabProps {
  legalRep: LegalRepresentative | undefined;
  fieldRejections?: Record<string, FieldRejectionInfo>;
  onShowFieldReason?: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function LegalRepresentativeTab({
  legalRep,
  fieldRejections,
  onShowFieldReason,
  applicationId,
  t,
}: LegalRepresentativeTabProps) {
  return (
    <View className="max-w-4xl items-start">
      <AppText weight="bold" className="text-xl text-dark-text-50 mb-2">
        {t("review.application.legalRepresentative.title")}
      </AppText>
      <AppText className="text-base text-dark-text-20 mb-8 leading-6">
        {t("review.application.legalRepresentative.description")}
      </AppText>

      <View className="h-px bg-stroke-20 mb-8 w-full" />

      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.legalRepresentative.fields.fullNameChinese")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.fullNameChinese}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.legalRepresentative.fields.fullNameEnglish")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.fullNameEnglish}
            </AppText>
          </DetailField>
        </View>
      </View>

      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.legalRepresentative.fields.nationality")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.nationality}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6" />
      </View>

      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.legalRepresentative.fields.role")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.role}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.legalRepresentative.fields.department")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.department}
            </AppText>
          </DetailField>
        </View>
      </View>

      <View className="flex-col tablet:flex-row w-full">
        <View className="tablet:flex-1 tablet:pe-6">
          <DetailField label={t("review.application.legalRepresentative.fields.phoneNumber")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.phone}
            </AppText>
          </DetailField>
        </View>
        <View className="tablet:flex-1 tablet:ps-6">
          <DetailField label={t("review.application.legalRepresentative.fields.businessEmail")}>
            <AppText weight="medium" className="text-base text-dark-text-50">
              {legalRep?.email}
            </AppText>
          </DetailField>
        </View>
      </View>

      <View className="flex-col w-full">
        <DetailField label={t("review.application.legalRepresentative.fields.legalId")}>
          <AppText weight="medium" className="text-base text-dark-text-50 mb-4">
            {legalRep?.idType ?? t("review.application.legalRepresentative.passport")}
          </AppText>
          {legalRep?.idDocumentUrl ? (
            <FilePreview
              className="max-w-[320px]"
              fileKey={legalRep?.idDocumentUrl}
              isVendor={false}
              applicationId={applicationId}
            />
          ) : (
            <View className="max-w-[320px] h-[200px] bg-white rounded-lg border border-stroke-30 items-center justify-center">
              <AppText className="text-sm text-dark-text-20 text-center">
                {t("review.application.legalRepresentative.notUploaded")}
              </AppText>
            </View>
          )}
          {fieldRejections?.["id_type"] && onShowFieldReason && (
            <FieldRejectedBadge onShowReason={() => onShowFieldReason("id_type")} t={t} />
          )}
        </DetailField>
      </View>

      <View className="flex-col mt-4 w-full">
        <DetailField label={t("review.application.legalRepresentative.fields.wechatQr")}>
          {legalRep?.wechatQrUrl ? (
            <FilePreview
              className="max-w-[320px]"
              fileKey={legalRep?.wechatQrUrl}
              isVendor={false}
              applicationId={applicationId}
            />
          ) : (
            <AppText className="text-base text-dark-text-20 italic">
              {t("review.application.legalRepresentative.notUploaded")}
            </AppText>
          )}
        </DetailField>
      </View>
    </View>
  );
}
